import React, {
  startTransition,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import {
  FaEdit,
  FaEye,
  FaFilter,
  FaKey,
  FaPlus,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUser,
  FaUsers
} from 'react-icons/fa';
import EditUserDialog from '../components/EditUserDialog';
import { AuthContext } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import userService from '../services/userService';
import { formatDate, formatNumber } from '../utils/format';
import {
  getAssignableRolesForActor,
  isAdminLike,
  normalizeRole
} from '../utils/rbac';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeManagedRole = (role) => {
  if (!role) return 'technician';
  return normalizeRole(String(role).trim().toLowerCase().replace(/\s+/g, '_'));
};

const ROLE_OPTIONS = [
  { value: 'technician', label: 'فني' },
  { value: 'supervisor', label: 'مشرف' },
  { value: 'admin', label: 'مدير النظام' },
  { value: 'viewer', label: 'مستخدم عرض' }
];

const EMPTY_CREATE_FORM = {
  username: '',
  full_name: '',
  email: '',
  phone: '',
  role: 'technician',
  password: ''
};

const sortUsersByName = (items = []) => (
  [...items].sort((left, right) => (
    String(left?.full_name || '').localeCompare(String(right?.full_name || ''), 'ar')
  ))
);

const UserList = () => {
  const isMountedRef = useRef(true);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [viewModalShow, setViewModalShow] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const { user: currentUser, updateCurrentUser } = useContext(AuthContext);
  const { addNotification } = useNotification();
  const currentRole = normalizeRole(currentUser?.role);
  const adminMode = isAdminLike(currentRole);
  const assignableRoles = getAssignableRolesForActor(currentRole);

  const selectableRoleOptions = useMemo(() => {
    const nextOptions = ROLE_OPTIONS.filter((option) => assignableRoles.includes(option.value));
    return nextOptions.length > 0 ? nextOptions : ROLE_OPTIONS.filter((option) => option.value === 'technician');
  }, [assignableRoles]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setListError('');
        const data = await userService.getAll();
        if (!isMountedRef.current) return;
        setUsers(sortUsersByName(data.users || data));
      } catch (error) {
        console.error(error);
        if (!isMountedRef.current) return;
        const message = error.response?.data?.error || 'تعذر تحميل قائمة المستخدمين';
        setListError(message);
        addNotification(message, 'error', 'فشل التحميل');
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadUsers();
  }, [addNotification]);

  useEffect(() => {
    if (selectableRoleOptions.some((option) => option.value === form.role)) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      role: selectableRoleOptions[0]?.value || 'technician'
    }));
  }, [form.role, selectableRoleOptions]);

  const canEditUser = (targetUser) => {
    if (adminMode) return true;
    return Number(currentUser?.id) === Number(targetUser?.id);
  };

  const updateUsersState = (updater) => {
    startTransition(() => {
      setUsers((previousUsers) => sortUsersByName(updater(previousUsers)));
    });
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟ سيتم فقدان جميع صلاحياته وبياناته.')) {
      return;
    }

    try {
      await userService.delete(userId);
      updateUsersState((previousUsers) => previousUsers.filter((user) => Number(user.id) !== Number(userId)));
      addNotification('تم حذف المستخدم بنجاح', 'success', 'تم الحذف');
    } catch (error) {
      console.error(error);
      addNotification(error.response?.data?.error || 'فشل حذف المستخدم', 'error', 'تعذر الحذف');
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previousForm) => ({ ...previousForm, [name]: value }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setFormError('');
    setFormSuccess('');
    setFormLoading(true);

    try {
      const payload = {
        username: form.username.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        role: normalizeManagedRole(form.role),
        password: form.password
      };

      if (!payload.username || !payload.full_name || !payload.email || !payload.password) {
        setFormError('الرجاء تعبئة جميع الحقول الإلزامية');
        setFormLoading(false);
        return;
      }

      if (!EMAIL_REGEX.test(payload.email)) {
        setFormError('صيغة البريد الإلكتروني غير صحيحة');
        setFormLoading(false);
        return;
      }

      if (payload.password.length < 6) {
        setFormError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
        setFormLoading(false);
        return;
      }

      const result = await userService.create(payload);
      const createdUser = result.user;

      updateUsersState((previousUsers) => [...previousUsers, createdUser]);
      setForm(EMPTY_CREATE_FORM);
      setFormSuccess(result.message || 'تم إضافة المستخدم بنجاح');
      addNotification(result.message || 'تمت إضافة المستخدم بنجاح', 'success', 'حفظ المستخدم');
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.error || 'حدث خطأ أثناء إضافة المستخدم';
      setFormError(message);
      addNotification(message, 'error', 'تعذر الحفظ');
    } finally {
      if (isMountedRef.current) {
        setFormLoading(false);
      }
    }
  };

  const handleShowView = (user) => {
    setEditingUser(user);
    setViewModalShow(true);
  };

  const handleCloseView = () => {
    setEditingUser(null);
    setViewModalShow(false);
  };

  const handleShowResetPassword = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setResetPasswordModal(true);
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (!newPassword || newPassword.length < 6) {
      addNotification('كلمة المرور يجب أن تكون 6 أحرف على الأقل', 'warning', 'تحقق من البيانات');
      return;
    }

    setResetPasswordLoading(true);

    try {
      await userService.adminResetPassword(resetPasswordUser.id, newPassword);
      setResetPasswordModal(false);
      setResetPasswordUser(null);
      setNewPassword('');
      addNotification('تمت إعادة تعيين كلمة المرور بنجاح', 'success', 'إعادة تعيين');
    } catch (error) {
      console.error(error);
      addNotification(error.response?.data?.error || 'فشل في إعادة تعيين كلمة المرور', 'error', 'تعذر التعيين');
    } finally {
      if (isMountedRef.current) {
        setResetPasswordLoading(false);
      }
    }
  };

  const handleShowEdit = (user) => {
    setEditingUser(user);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
  };

  const handleEditSuccess = async (updatedUser, response) => {
    updateUsersState((previousUsers) => previousUsers.map((user) => (
      Number(user.id) === Number(updatedUser.id)
        ? { ...user, ...updatedUser }
        : user
    )));

    if (Number(currentUser?.id) === Number(updatedUser.id)) {
      updateCurrentUser(response?.auth_user || {
        ...currentUser,
        ...updatedUser,
        role: currentUser?.role
      });
    }

    setFormSuccess('');
    setFormError('');
    setEditDialogOpen(false);
    setEditingUser(null);
    addNotification('تم تحديث بيانات المستخدم بنجاح', 'success', 'تم الحفظ');
  };

  const getRoleBadge = (role) => {
    const normalized = normalizeRole(role);

    const variants = {
      admin: 'danger',
      supervisor: 'warning',
      technician: 'primary'
    };

    const labels = {
      admin: 'مدير النظام',
      supervisor: 'مشرف',
      technician: 'فني'
    };

    return <Badge bg={variants[normalized] || 'secondary'}>{labels[normalized] || normalized}</Badge>;
  };

  const filteredUsers = useMemo(() => {
    const normalizedSearch = deferredSearchTerm.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch = !normalizedSearch || [
        user.full_name,
        user.username,
        user.email,
        user.phone
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch));

      const matchesStatus =
        !statusFilter ||
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);

      return matchesSearch && matchesStatus;
    });
  }, [deferredSearchTerm, statusFilter, users]);

  const hasActiveFilters = Boolean(searchTerm.trim() || statusFilter);
  const selectedUserCanManageRole = adminMode && Number(currentUser?.id) !== Number(editingUser?.id);
  const selectedUserCanManageStatus = adminMode && Number(currentUser?.id) !== Number(editingUser?.id);

  if (loading) {
    return (
      <Container fluid className="page-container">
        <div className="dashboard-header-modern">
          <div className="header-content">
            <div className="header-icon">
              <FaUsers />
            </div>
            <div>
              <h1 className="header-title">المستخدمون</h1>
              <p className="header-subtitle">إدارة حسابات المستخدمين</p>
            </div>
          </div>
        </div>
        <Card className="dashboard-card">
          <Card.Body className="p-4">
            <div className="skeleton skeleton-card" />
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="page-container user-list-container">
      <div className="dashboard-header-modern">
        <div className="header-content">
          <div className="header-icon">
            <FaUsers />
          </div>
          <div>
            <h1 className="header-title">المستخدمون</h1>
            <p className="header-subtitle">
              إدارة حسابات المستخدمين ({filteredUsers.length} من {users.length} مستخدم)
            </p>
          </div>
        </div>
      </div>

      {listError && (
        <Alert variant="danger" className="alert-modern mb-4">
          {listError}
        </Alert>
      )}

      <Card className="dashboard-card management-filter-card mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={8}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">البحث في المستخدمين</Form.Label>
                <div className="search-wrapper">
                  <span className="search-icon"><FaSearch /></span>
                  <Form.Control
                    type="text"
                    placeholder="ابحث بالاسم، اسم المستخدم، البريد، أو الهاتف"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="search-input form-control-custom"
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">حالة الحساب</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="form-select-custom"
                >
                  <option value="">الكل</option>
                  <option value="active">نشط</option>
                  <option value="inactive">غير نشط</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="management-filter-actions mt-3">
            {hasActiveFilters && (
              <Button
                className="btn-action-cancel"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
              >
                <FaTimes className="btn-icon" /> مسح الفلاتر
              </Button>
            )}
            <span className="text-muted small d-inline-flex align-items-center gap-2">
              <FaFilter /> نتائج العرض: {filteredUsers.length}
            </span>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-4 management-split-layout">
        {adminMode && (
          <Col lg={5}>
            <Card className="dashboard-card user-form-card">
              <Card.Body className="p-4">
                <div className="form-section-header">
                  <FaPlus className="section-icon" />
                  <h3>إضافة مستخدم جديد</h3>
                </div>

                {formError && <Alert variant="danger" className="alert-modern">{formError}</Alert>}
                {formSuccess && <Alert variant="success" className="alert-modern">{formSuccess}</Alert>}

                <Form onSubmit={handleCreateUser}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="form-field">
                        <Form.Label className="field-label">اسم المستخدم *</Form.Label>
                        <Form.Control
                          name="username"
                          value={form.username}
                          onChange={handleChange}
                          placeholder="مثال: tech001"
                          disabled={formLoading}
                          className="form-field-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="form-field">
                        <Form.Label className="field-label">الاسم الكامل *</Form.Label>
                        <Form.Control
                          name="full_name"
                          value={form.full_name}
                          onChange={handleChange}
                          placeholder="الاسم الثلاثي"
                          disabled={formLoading}
                          className="form-field-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="form-field">
                        <Form.Label className="field-label">البريد الإلكتروني *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="example@company.com"
                          disabled={formLoading}
                          className="form-field-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="form-field">
                        <Form.Label className="field-label">الهاتف</Form.Label>
                        <Form.Control
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="09XXXXXXXX"
                          disabled={formLoading}
                          className="form-field-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="form-field">
                        <Form.Label className="field-label">الصلاحية</Form.Label>
                        <Form.Select
                          name="role"
                          value={form.role}
                          onChange={handleChange}
                          className="form-field-input"
                          disabled={formLoading}
                        >
                          {selectableRoleOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="form-field">
                        <Form.Label className="field-label">كلمة المرور *</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={form.password}
                          onChange={handleChange}
                          placeholder="كلمة مرور قوية"
                          disabled={formLoading}
                          className="form-field-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="form-actions">
                    <Button type="submit" className="btn-action-create" disabled={formLoading}>
                      <FaSave className="btn-icon" />
                      {formLoading ? 'جاري الحفظ...' : 'حفظ المستخدم'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        )}

        <Col lg={adminMode ? 7 : 12}>
          <Card className="dashboard-card user-table-card">
            <Card.Body className="p-0">
              <div className="table-header-section">
                <div className="form-section-header">
                  <FaUsers className="section-icon" />
                  <h3>قائمة المستخدمين</h3>
                </div>
              </div>

              {filteredUsers.length === 0 ? (
                <div className="table-empty-state">
                  <div className="table-empty-state-icon">
                    <FaUser size={48} />
                  </div>
                  <h5 className="empty-state-title">لا توجد نتائج</h5>
                  <p className="empty-state-description">
                    {hasActiveFilters ? 'لا يوجد مستخدمون مطابقون للفلاتر الحالية' : 'لم تتم إضافة أي مستخدمين بعد'}
                  </p>
                  {hasActiveFilters && (
                    <Button className="btn-action-cancel" onClick={() => { setSearchTerm(''); setStatusFilter(''); }}>
                      <FaTimes className="btn-icon" /> مسح الفلاتر
                    </Button>
                  )}
                </div>
              ) : (
                <div className="table-responsive-wrapper responsive-table-wrapper">
                  <table className="modern-table responsive-data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>المستخدم</th>
                        <th>الصلاحية</th>
                        <th>الحالة</th>
                        <th>تاريخ الإنشاء</th>
                        <th className="text-center">إجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user, index) => (
                        <tr key={user.id}>
                          <td data-label="#"><span className="text-muted">{formatNumber(index + 1)}</span></td>
                          <td data-label="المستخدم">
                            <div className="cell-with-icon">
                              <div className="avatar avatar-sm">{(user.full_name || user.username || '?').charAt(0)}</div>
                              <div>
                                <div className="fw-bold">{user.full_name}</div>
                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>{user.username}</div>
                              </div>
                            </div>
                          </td>
                          <td data-label="الصلاحية">{getRoleBadge(user.role)}</td>
                          <td data-label="الحالة">
                            <span className={`status-badge ${user.is_active ? 'status-badge-resolved' : 'status-badge-hold'}`}>
                              {user.is_active ? 'نشط' : 'غير نشط'}
                            </span>
                          </td>
                          <td data-label="تاريخ الإنشاء">
                            <span className="text-muted">{formatDate(user.created_at)}</span>
                          </td>
                          <td data-label="إجراءات" className="table-cell-actions">
                            <div className="table-actions">
                              <Button
                                className="btn-action-view"
                                onClick={() => handleShowView(user)}
                                title={`عرض بيانات ${user.full_name}`}
                              >
                                <FaEye size={14} />
                                <span>عرض</span>
                              </Button>

                              {canEditUser(user) && (
                                <Button
                                  className="btn-action-edit"
                                  onClick={() => handleShowEdit(user)}
                                  title={`تعديل بيانات ${user.full_name}`}
                                >
                                  <FaEdit size={14} />
                                  <span>تعديل</span>
                                </Button>
                              )}

                              {adminMode && Number(currentUser?.id) !== Number(user.id) && normalizeManagedRole(user.role) !== 'admin' && (
                                <>
                                  <Button
                                    className="btn-action-reset"
                                    onClick={() => handleShowResetPassword(user)}
                                    title={`إعادة تعيين كلمة المرور لـ ${user.full_name}`}
                                  >
                                    <FaKey size={14} />
                                    <span>إعادة تعيين</span>
                                  </Button>
                                  <Button
                                    className="btn-action-delete"
                                    onClick={() => handleDeleteUser(user.id)}
                                    title={`حذف المستخدم ${user.full_name}`}
                                  >
                                    <FaTrash size={14} />
                                    <span>حذف</span>
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={viewModalShow} onHide={handleCloseView} centered>
        <Modal.Header closeButton className="modal-header-modern">
          <Modal.Title>
            <FaEye className="ms-2" /> تفاصيل المستخدم
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingUser && (
            <div>
              <Row>
                <Col md={6}>
                  <p><strong>اسم المستخدم:</strong> {editingUser.username}</p>
                </Col>
                <Col md={6}>
                  <p><strong>الاسم الكامل:</strong> {editingUser.full_name}</p>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <p><strong>البريد الإلكتروني:</strong> {editingUser.email}</p>
                </Col>
                <Col md={6}>
                  <p><strong>الهاتف:</strong> {editingUser.phone || 'غير متوفر'}</p>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <p><strong>الصلاحية:</strong> {getRoleBadge(editingUser.role)}</p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>الحالة:</strong>{' '}
                    <span className={`status-badge ${editingUser.is_active ? 'status-badge-resolved' : 'status-badge-hold'}`}>
                      {editingUser.is_active ? 'نشط' : 'غير نشط'}
                    </span>
                  </p>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={resetPasswordModal}
        onHide={() => {
          if (!resetPasswordLoading) {
            setResetPasswordModal(false);
            setResetPasswordUser(null);
          }
        }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="modal-header-modern">
          <Modal.Title>
            <FaKey className="ms-2" /> إعادة تعيين كلمة المرور
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleResetPassword}>
            <Form.Group className="form-field">
              <Form.Label className="field-label">المستخدم</Form.Label>
              <Form.Control value={resetPasswordUser?.full_name || ''} disabled className="form-field-input" />
            </Form.Group>

            <Form.Group className="form-field">
              <Form.Label className="field-label">كلمة المرور الجديدة</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="أدخل كلمة مرور جديدة (6 أحرف على الأقل)"
                className="form-field-input"
                disabled={resetPasswordLoading}
              />
            </Form.Group>

            <div className="form-actions">
              <Button
                variant="secondary"
                className="btn-action-cancel"
                onClick={() => {
                  if (!resetPasswordLoading) {
                    setResetPasswordModal(false);
                    setResetPasswordUser(null);
                  }
                }}
                disabled={resetPasswordLoading}
              >
                <FaTimes className="btn-icon" /> إلغاء
              </Button>
              <Button variant="primary" className="btn-action-save" type="submit" disabled={resetPasswordLoading}>
                <FaKey className="btn-icon" />
                {resetPasswordLoading ? 'جاري التعيين...' : 'إعادة التعيين'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <EditUserDialog
        open={editDialogOpen}
        user={editingUser}
        roleOptions={selectableRoleOptions}
        canManageRole={selectedUserCanManageRole}
        canManageStatus={selectedUserCanManageStatus}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
      />
    </Container>
  );
};

export default UserList;
